import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import { toFilters } from '@/api/customers';
import type { AttachmentPayload } from '@/api/uploads';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Contracts — global standalone module (`modules/contracts` on the v4 backend).
 * Two data domains: customer contracts (sell-side, `/contracts/customers`) and
 * partner contracts (buy-side, `/contracts/partners`). Field names / endpoints
 * are lifted verbatim from the backend so requests hit unchanged. Shapes below
 * were verified against live staging (`api-staging-datacentral`).
 */

/* ── Enums ─────────────────────────────────────────────────────────────── */

/** DB `contract_status` (`retainted` is the backend's spelling — verified live). */
export type ContractStatus =
  | 'pending'
  | 'exchanging'
  | 'uploading'
  | 'active'
  | 'outdate'
  | 'retainted'
  | 'expired';

export type ContractRole =
  | 'customer'
  | 'supplier'
  | 'reseller'
  | 'affiliate'
  | 'collaborator';

/** `earn` = money in (output), `pay` = money out (input). */
export type ContractDirection = 'earn' | 'pay';

/* ── Types ─────────────────────────────────────────────────────────────── */

/** A service line embedded on a contract row (`services[]`). */
export interface ContractServiceLine {
  service_id: number;
  service_name: string;
  product_group_name?: string;
  contract_value?: number;
  start_date?: string;
  end_date?: string;
}

/** An attachment reference embedded on a contract row (`attachments[]`). */
export interface ContractAttachment {
  attachment_id?: number;
  /** Canonical name field (upload-pipeline naming). */
  attachment_file_name?: string;
  /** Canonical size field in bytes (upload-pipeline naming). */
  attachment_file_size?: number;
  /** Canonical URL / storage path. */
  attachment_file_url?: string;
  /** Legacy aliases — present on some endpoints; checked as fallback. */
  attachment_name?: string;
  attachment_url?: string;
  created_at?: string;
  [key: string]: unknown;
}

/**
 * `GET /contracts/customers` row (`GetCustomerContractsRow`). Superset used by
 * both the list and (with `customer_language`) the detail record.
 */
export interface ContractRow {
  contract_id: number;
  contract_key: string | null;
  contract_number_id: string;
  contract_note: string | null;
  contract_status: ContractStatus;
  contract_type: string;
  contract_effective_date: string;
  contract_expiry_date: string | null;
  contract_value: number;
  contract_hidden?: boolean;
  contract_products?: unknown;
  contract_role: ContractRole;
  contract_direction: ContractDirection;
  employee_id?: number;
  employee_name?: string;
  customer_id?: number;
  customer_name?: string;
  company_name?: string | null;
  customer_contact_id?: number;
  customer_contact_name?: string | null;
  service_id?: number;
  product_group_name?: string;
  services?: ContractServiceLine[];
  attachments?: ContractAttachment[];
  sla_enable?: boolean;
  nda_enable?: boolean;
  auto_renewable?: boolean;
  is_renewable?: boolean;
  renewable_count?: number;
  first_contract_effective_date?: string;
  first_contract_expiry_date?: string;
  registration_date?: string | null;
  partner_id?: number | null;
  partner_name?: string | null;
  partner_code?: string | null;
  partner_email?: string | null;
  partner_phone?: string | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

/** `GET /contracts/customers/:id` — adds `customer_language`. */
export interface ContractDetail extends ContractRow {
  customer_language?: string;
}

/** `GET /contracts/customers/statistical` — stat-band counts + deltas. */
export interface ContractStatistical {
  total_count: number;
  created_count: number;
  exchanging_count: number;
  uploading_count: number;
  active_count: number;
  expired_count: number;
  delta_total: number;
  delta_created: number;
  delta_exchanging: number;
  delta_uploading: number;
  delta_active: number;
  delta_expired: number;
  /** Percentage deltas as decimal numbers (e.g. `-23.81` = −23.81%). */
  delta_total_percent: number;
  delta_created_percent: number;
  delta_exchanging_percent: number;
  delta_uploading_percent: number;
  delta_active_percent: number;
  delta_expired_percent: number;
}

/** `GET /contracts/customers/:id/updates` row. `metadata` is an opaque diff blob. */
export interface ContractUpdateRow {
  contract_update_type?: string;
  contract_update_description?: string;
  employee_name?: string;
  metadata?: unknown;
  created_at?: string;
}

/** `GET /contracts/customers/:id/subcontracts` row. */
export interface SubcontractRow {
  subcontract_id: number;
  subcontract_code: string;
  subcontract_name?: string;
  subcontract_status?: ContractStatus;
  subcontract_effective_date?: string;
  subcontract_expiry_date?: string;
  contract_id: number;
  contract_value?: number;
}

/** `GET /contracts/customers/:id/processes` row (workflow step). */
export interface ContractProcessRow {
  process_id?: number;
  process_code?: string;
  process_name?: string;
  process_status?: string;
  process_type?: string;
  employee_name?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

/** `GET /contracts/partners` row (buy-side; lighter DTO — no role/direction). */
export interface PartnerContractRow {
  contract_id: number;
  contract_number_id: string;
  contract_key?: string | null;
  contract_note?: string | null;
  contract_status: ContractStatus;
  contract_effective_date: string;
  contract_expiry_date: string | null;
  contract_value: number;
  employee_id?: number;
  employee_name?: string;
  partner_id?: number;
  partner_name?: string;
  partner_code?: string;
  partner_email?: string;
  partner_phone?: string;
  attachments?: ContractAttachment[];
  created_at?: string;
}

/* ── Params ────────────────────────────────────────────────────────────── */

export interface ContractListParams {
  page?: number;
  page_size?: number;
  q?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  /** Structured filters, base64-encoded into the `filters` query param. */
  filters?: Record<string, unknown>;
}

function listQuery(params: ContractListParams): string {
  const { filters, ...rest } = params;
  const q: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== '') q[k] = v as string | number;
  }
  if (filters && Object.keys(filters).length > 0) q.filters = toFilters(filters);
  return qs.stringify(q);
}

/* ── Query keys ────────────────────────────────────────────────────────── */

export const contractKeys = {
  all: ['contracts'] as const,
  list: (params: ContractListParams) => ['contracts', 'customers', 'list', params] as const,
  statistical: (params: ContractListParams) => ['contracts', 'customers', 'statistical', params] as const,
  detail: (id: number) => ['contracts', 'customers', 'detail', id] as const,
  updates: (id: number, params: ContractListParams) => ['contracts', 'customers', id, 'updates', params] as const,
  subcontracts: (id: number, params: ContractListParams) => ['contracts', 'customers', id, 'subcontracts', params] as const,
  processes: (id: number) => ['contracts', 'customers', id, 'processes'] as const,
  suggestions: (id: number) => ['contracts', 'customers', id, 'suggestions'] as const,
  overview: (params: ContractListParams) => ['contracts', 'customers', 'overview', params] as const,
  partners: (params: ContractListParams) => ['contracts', 'partners', 'list', params] as const,
  partnerDetail: (id: number) => ['contracts', 'partners', 'detail', id] as const,
};

/* ── Customer contracts ────────────────────────────────────────────────── */

/** `GET /contracts/customers` — global customer (sell-side) contract list. */
export function useContracts(params: ContractListParams = {}) {
  return useQuery({
    queryKey: contractKeys.list(params),
    queryFn: async (): Promise<ResponseArray<ContractRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ContractRow>>(
        `/contracts/customers?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /contracts/customers/statistical` — stat-band counts + deltas. */
export function useContractStatistical(params: ContractListParams = {}) {
  return useQuery({
    queryKey: contractKeys.statistical(params),
    queryFn: async (): Promise<ResponseObject<ContractStatistical>> => {
      const res = await dataCentralApi.get<ResponseObject<ContractStatistical>>(
        `/contracts/customers/statistical?${listQuery(params)}`,
      );
      return res.data;
    },
  });
}

/** `GET /contracts/customers/:id` — full contract record. */
export function useContractDetail(id: number) {
  return useQuery({
    queryKey: contractKeys.detail(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<ContractDetail>> => {
      const res = await dataCentralApi.get<ResponseObject<ContractDetail>>(
        `/contracts/customers/${id}`,
      );
      return res.data;
    },
  });
}

/** `GET /contracts/customers/:id/updates` — change history log. */
export function useContractUpdates(id: number, params: ContractListParams = {}) {
  return useQuery({
    queryKey: contractKeys.updates(id, params),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ContractUpdateRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ContractUpdateRow>>(
        `/contracts/customers/${id}/updates?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /contracts/customers/:id/subcontracts` — child contracts. */
export function useContractSubcontracts(id: number, params: ContractListParams = {}) {
  return useQuery({
    queryKey: contractKeys.subcontracts(id, params),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<SubcontractRow>> => {
      const res = await dataCentralApi.get<ResponseArray<SubcontractRow>>(
        `/contracts/customers/${id}/subcontracts?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /contracts/customers/:id/processes` — workflow steps. */
export function useContractProcesses(id: number, params: ContractListParams = {}) {
  return useQuery({
    queryKey: contractKeys.processes(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ContractProcessRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ContractProcessRow>>(
        `/contracts/customers/${id}/processes?${listQuery({ page: 1, page_size: 50, ...params })}`,
      );
      return res.data;
    },
  });
}

/* ── Suggestions ────────────────────────────────────────────────────────── */

/**
 * A suggested (sample) contract row returned by
 * `GET /contracts/customers/:id/suggestions`. Field names assumed from the
 * phase spec — confirm shape via `node scripts/probe.mjs /contracts/customers/<id>/suggestions`.
 */
export interface SampleContract {
  contract_id: number;
  contract_number_id: string;
  contract_key?: string | null;
  contract_type?: string;
  contract_status?: ContractStatus;
  product_group_name?: string;
  contract_note?: string | null;
  employee_name?: string;
  attachments?: ContractAttachment[];
  updated_at?: string;
  [key: string]: unknown;
}

/** `GET /contracts/customers/:id/suggestions` — sample contracts that may apply. */
export function useContractSuggestions(id: number) {
  return useQuery({
    queryKey: contractKeys.suggestions(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<SampleContract>> => {
      const res = await dataCentralApi.get<ResponseArray<SampleContract>>(
        `/contracts/customers/${id}/suggestions`,
      );
      return res.data;
    },
  });
}

/* ── Overview ───────────────────────────────────────────────────────────── */

/**
 * A contract overview bucket — one entry per `contract_status` grouping.
 * `GET /contracts/customers/overview` returns the full dataset (no pagination);
 * cards are grouped in the UI. Field names assumed — confirm via probe.
 */
export interface ContractOverviewItem {
  contract_id: number;
  contract_number_id: string;
  contract_key?: string | null;
  contract_status: ContractStatus;
  contract_type?: string;
  contract_expiry_date?: string | null;
  customer_id?: number;
  customer_name?: string;
  company_name?: string | null;
  customer_industry?: string | null;
  employee_id?: number;
  employee_name?: string;
  services?: ContractServiceLine[];
  sla_enable?: boolean;
  nda_enable?: boolean;
  [key: string]: unknown;
}

/** Params for the overview endpoint (filter-only, no pagination). */
export interface ContractOverviewParams {
  filters?: Record<string, unknown>;
}

function overviewQuery(params: ContractOverviewParams): string {
  if (!params.filters || !Object.keys(params.filters).length) return '';
  return `filters=${toFilters(params.filters)}`;
}

/** `GET /contracts/customers/overview` — kanban overview grouped by status. */
export function useContractOverview(params: ContractOverviewParams = {}) {
  return useQuery({
    queryKey: contractKeys.overview(params as ContractListParams),
    queryFn: async (): Promise<ResponseArray<ContractOverviewItem>> => {
      const qs = overviewQuery(params);
      const res = await dataCentralApi.get<ResponseArray<ContractOverviewItem>>(
        `/contracts/customers/overview${qs ? `?${qs}` : ''}`,
      );
      return res.data;
    },
  });
}

/** `GET /contracts/customers/overdue/export` — download XLSX blob of overdue contracts. */
export async function exportOverdueContracts(): Promise<Blob> {
  const res = await dataCentralApi.get('/contracts/customers/overdue/export', {
    responseType: 'blob',
  });
  return res.data as Blob;
}

/* ── Partner contracts ─────────────────────────────────────────────────── */

/** `GET /contracts/partners` — buy-side (supplier) contract list. */
export function usePartnerContracts(params: ContractListParams = {}) {
  return useQuery({
    queryKey: contractKeys.partners(params),
    queryFn: async (): Promise<ResponseArray<PartnerContractRow>> => {
      const res = await dataCentralApi.get<ResponseArray<PartnerContractRow>>(
        `/contracts/partners?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /contracts/partners/:id` — partner contract record. */
export function usePartnerContractDetail(id: number) {
  return useQuery({
    queryKey: contractKeys.partnerDetail(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<PartnerContractRow>> => {
      const res = await dataCentralApi.get<ResponseObject<PartnerContractRow>>(
        `/contracts/partners/${id}`,
      );
      return res.data;
    },
  });
}

/* ── Create ────────────────────────────────────────────────────────────── */

/**
 * The eight service-time dates + statement for one product line. Legacy sends
 * all eight as `YYYY-MM-DD` strings; the backend binds them to `pgtype.Date`.
 */
export interface ContractServiceTimes {
  first_startdate: string;
  first_enddate: string;
  continue_startdate: string;
  continue_enddate: string;
  statement_startdate?: string;
  statement_enddate?: string;
  current_startdate?: string;
  current_enddate?: string;
  statement?: string;
}

/**
 * One entry in a product's `service_configoptions` — the per-type pricing/config
 * unit (bandwidth commitment, warehouse capacity, CDN region, …). Deliberately
 * open: each product-type template fills the fields it needs. See the product
 * template spec for the full 24-field superset.
 */
export interface ContractServiceConfigOption {
  name?: string;
  type?: string;
  unit?: string;
  commitment_value?: number;
  commitment_time?: number;
  commitment_price_per_unit?: number;
  commitment_prepaid?: boolean;
  capacity_value?: number;
  capacity_unit?: string;
  brand_name?: string;
  model_name?: string;
  scope?: string;
  resizable?: boolean;
  statementable?: boolean;
  price_config?: Record<string, unknown>;
  prices?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

/** A product line inside the create `order` — the full legacy `Product` shape. */
export interface ContractOrderProduct {
  product_id: number;
  product_name: string;
  product_group_code?: string;
  product_group_name: string;
  service_price_model?: string;
  service_price_register?: number;
  service_price_recurring?: number;
  prepaid?: boolean;
  /** Dispatches the per-type template (e.g. "Config.Bandwidth", "Cloud.VCloud"). */
  template_name?: string;
  service_configoptions?: ContractServiceConfigOption[];
  service_times?: ContractServiceTimes;
  service_domain_metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

/** SLA config attached when `sla_enable` (mirrors backend `ContractServiceSLAParams`). */
export interface ContractServiceSla {
  sla_template_id?: number | null;
  sla_source: 'template' | 'custom';
  effective_from?: string;
  effective_to?: string;
  uptime_guarantee?: number | null;
  downtime_allowance_minutes?: number | null;
  business_hours_only?: boolean | null;
  business_hours_config?: Record<string, unknown> | null;
  sla_rules?: Array<Record<string, unknown>>;
  escalation_rules?: Array<Record<string, unknown>>;
  custom_terms?: Array<Record<string, unknown>>;
}

/** `POST /contracts/customers` payload — the full legacy `ContractCreateParams`. */
export interface ContractCreatePayload {
  use_contract_id?: boolean;
  contract_id?: number;
  partner_id?: number | null;
  contract_note?: string;
  contract_effective_date: string;
  contract_expiry_date: string;
  contract_value?: number;
  customer_id: number;
  customer_journey_id?: number;
  customer_contact_id?: number | null;
  contract_role: ContractRole;
  contract_direction: ContractDirection;
  sla_enable?: boolean;
  nda_enable?: boolean;
  auto_renewable?: boolean;
  currency_code?: string;
  exchange_rate?: number;
  order: {
    order_source: string;
    /** Backend field is `order_source_name` (NOT `order_name`). */
    order_source_name: string;
    order_note?: string;
    order_amount?: number;
    products: ContractOrderProduct[];
  };
  /** Included only when `sla_enable` is true. */
  service_sla?: ContractServiceSla;
  /** Included only when `nda_enable` is true. */
  nda_attachments?: AttachmentPayload[];
}

/** `POST /contracts/customers` — create a customer contract. */
export function useCreateContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ContractCreatePayload): Promise<ResponseObject<ContractDetail>> => {
      const res = await dataCentralApi.post<ResponseObject<ContractDetail>>(
        '/contracts/customers',
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
}

/* ── Lifecycle actions (status-gated) ──────────────────────────────────── */

/** `POST /contracts/customers/:id/renew` — renew an active contract. */
export function useRenewContract(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>): Promise<ResponseObject<ContractDetail>> => {
      const res = await dataCentralApi.post<ResponseObject<ContractDetail>>(
        `/contracts/customers/${id}/renew`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
}

/** `POST /contracts/customers/:id/terminate` — terminate a contract. */
export function useTerminateContract(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>): Promise<ResponseObject<ContractDetail>> => {
      const res = await dataCentralApi.post<ResponseObject<ContractDetail>>(
        `/contracts/customers/${id}/terminate`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
}

/** `POST /contracts/customers/:id/retention` — retain/clone with a reason. */
export function useRetainContract(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>): Promise<ResponseObject<ContractDetail>> => {
      const res = await dataCentralApi.post<ResponseObject<ContractDetail>>(
        `/contracts/customers/${id}/retention`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
}

/* ── Renew with dates ──────────────────────────────────────────────────── */

/** Payload for `POST /contracts/customers/:id/renew`. */
export interface ContractRenewPayload {
  /** `YYYY-MM-DD` — backend binds to pgtype.Date. */
  contract_effective_date: string;
  /** `YYYY-MM-DD` — backend binds to pgtype.Date. */
  contract_expiry_date: string;
  /** Required by the controller binding. */
  renewal_reason: string;
}

/* ── Update contract ──────────────────────────────────────────────────── */

/**
 * `PUT /contracts/customers/:id` payload — matches `RequestUpdateContractParams`
 * (contracts.customer.types.go). Only `contract` sub-object is required; send
 * only the fields that should change.
 */
export interface ContractUpdatePayload {
  contract: {
    contract_status?: ContractStatus;
    contract_role?: ContractRole;
    contract_direction?: ContractDirection;
    contract_note?: string;
    contract_effective_date?: string;
    contract_expiry_date?: string;
    contract_value?: number;
    sla_enable?: boolean;
    nda_enable?: boolean;
    auto_renewable?: boolean;
    is_renewable?: boolean;
    renewable_count?: number;
    partner_id?: number | null;
    contract_key?: string | null;
  };
  service_ids?: number[];
  attachments?: AttachmentPayload[];
  remove_attachments?: number[];
}

/** `PUT /contracts/customers/:id` — update an existing contract. */
export function useUpdateContract(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ContractUpdatePayload): Promise<ResponseObject<ContractDetail>> => {
      const res = await dataCentralApi.put<ResponseObject<ContractDetail>>(
        `/contracts/customers/${id}`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
}

/* ── Subcontract mutations ─────────────────────────────────────────────── */

/**
 * Payload for `POST /contracts/customers/:contractId/subcontracts`.
 * Wraps `SubcontractCreateParams` from the backend (`contracts.subcontracts.types.go`).
 * Outer `subcontract` key mirrors the Go struct field name.
 */
export interface SubcontractCreatePayload {
  subcontract: {
    subcontract_code?: string;
    subcontract_name?: string;
    subcontract_note?: string;
    subcontract_status?: ContractStatus;
    /** `YYYY-MM-DD` */
    subcontract_effective_date?: string;
    /** `YYYY-MM-DD` */
    subcontract_expiry_date?: string;
    contract_role?: ContractRole;
    contract_direction?: ContractDirection;
    sla_enable?: boolean;
    nda_enable?: boolean;
    auto_renewable?: boolean;
    is_renewable?: boolean;
  };
  service_ids?: number[];
  attachments?: AttachmentPayload[];
}

/** Payload for `PUT /contracts/customers/:contractId/subcontracts/:subcontractId`. */
export interface SubcontractUpdatePayload {
  subcontract: {
    subcontract_name?: string;
    subcontract_note?: string;
    subcontract_status?: ContractStatus;
    /** `YYYY-MM-DD` */
    subcontract_effective_date?: string;
    /** `YYYY-MM-DD` */
    subcontract_expiry_date?: string;
    contract_role?: ContractRole;
    contract_direction?: ContractDirection;
    sla_enable?: boolean;
    nda_enable?: boolean;
    auto_renewable?: boolean;
    is_renewable?: boolean;
  };
  service_ids?: number[];
  attachments?: AttachmentPayload[];
  attachments_to_delete?: number[];
}

/** `POST /contracts/customers/:contractId/subcontracts` — create a subcontract. */
export function useCreateSubcontract(contractId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SubcontractCreatePayload): Promise<ResponseObject<SubcontractRow>> => {
      const res = await dataCentralApi.post<ResponseObject<SubcontractRow>>(
        `/contracts/customers/${contractId}/subcontracts`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.subcontracts(contractId, {}) });
    },
  });
}

/** `PUT /contracts/customers/:contractId/subcontracts/:subcontractId` — update a subcontract. */
export function useUpdateSubcontract(contractId: number, subcontractId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SubcontractUpdatePayload): Promise<ResponseObject<SubcontractRow>> => {
      const res = await dataCentralApi.put<ResponseObject<SubcontractRow>>(
        `/contracts/customers/${contractId}/subcontracts/${subcontractId}`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.subcontracts(contractId, {}) });
    },
  });
}

/** `DELETE /contracts/customers/:contractId/subcontracts/:subcontractId` — delete a subcontract. */
export function useDeleteSubcontract(contractId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ subcontractId, reason }: { subcontractId: number; reason?: string }): Promise<void> => {
      await dataCentralApi.delete(
        `/contracts/customers/${contractId}/subcontracts/${subcontractId}`,
        reason ? { params: { reason } } : undefined,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.subcontracts(contractId, {}) });
    },
  });
}

/* ── Process mutations ─────────────────────────────────────────────────── */

/**
 * Payload for `POST /contracts/customers/:contractId/processes`.
 * Outer `contract` key mirrors the Go struct field `ProcessParams db.CreateContractProcessParams json:"contract"`.
 */
export interface ContractProcessCreatePayload {
  contract: {
    contract_process_code?: string;
    contract_process_title?: string;
    contract_process_content?: string;
    contract_process_problem?: string;
    /** Employee responsible for this process step. */
    contract_process_employee_id?: number;
    contract_process_assignees?: number[];
    /** `pending` | `in-progress` | `completed` | `cancelled` | `rejected` */
    contract_process_status?: string;
    /** ISO timestamp string */
    contract_process_deadline?: string;
    /** `low` | `medium` | `high` | `critical` */
    contract_process_priority?: string;
    contract_processable_type?: string;
    contract_processable_id?: number;
  };
  attachments?: AttachmentPayload[];
}

/**
 * Payload for `POST /contracts/customers/:contractId/processes/:processId/histories`.
 * Inline struct from DeliveryContractProcess controller — binds `ContractProcessHistoryCreateParams`.
 */
export interface ContractProcessDeliveryPayload {
  /** Feedback text from the handler. */
  contract_process_feedback?: string;
  /** New status to set on the process (`ContractProcessStatus` enum). */
  contract_process_feedback_status?: string;
  attachments?: AttachmentPayload[];
}

/** `POST /contracts/customers/:contractId/processes` — create a process step. */
export function useCreateContractProcess(contractId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ContractProcessCreatePayload): Promise<ResponseObject<ContractProcessRow>> => {
      const res = await dataCentralApi.post<ResponseObject<ContractProcessRow>>(
        `/contracts/customers/${contractId}/processes`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.processes(contractId) });
    },
  });
}

/**
 * `DELETE /contracts/customers/:contractId/processes/:processId` — cancel (soft-delete)
 * a process step. The route is `DELETE /:contractProcessID` gated on `scopes.Cancel`.
 */
export function useDeleteContractProcess(contractId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (processId: number): Promise<void> => {
      await dataCentralApi.delete(
        `/contracts/customers/${contractId}/processes/${processId}`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.processes(contractId) });
    },
  });
}

/**
 * `POST /contracts/customers/:contractId/processes/:processId/archived` — archive a process.
 */
export function useArchiveContractProcess(contractId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (processId: number): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/contracts/customers/${contractId}/processes/${processId}/archived`,
        {},
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.processes(contractId) });
    },
  });
}

/**
 * `POST /contracts/customers/:contractId/processes/:processId/histories` — deliver
 * feedback on a process step (and optionally advance its status).
 */
export function useDeliverContractProcess(contractId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      processId,
      payload,
    }: {
      processId: number;
      payload: ContractProcessDeliveryPayload;
    }): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/contracts/customers/${contractId}/processes/${processId}/histories`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.processes(contractId) });
    },
  });
}
